import { metersPerNauticalMile } from "./constants";
import { uuidv4 } from "./helpers";
import { DefaultSectorValues, Sector, SectorColor } from "./lighthouseConstants";
import { NFSStatus } from "./nfsUtils";
import { applySectorOrder } from "./sectorRules";

export type LengthUnit = "Nm" | "m";

/**
 * A number as the user is typing it. `raw` is rendered verbatim and never reformatted while
 * typing; `meters` is the canonical value, and is what a unit switch regenerates the display
 * from - converting the displayed text instead would compound rounding on every flip.
 */
export interface NumericDraft {
    raw: string;
    meters: number | null;
}

/** One row of the sector definition table. */
export interface SectorDraft {
    key: string;
    /**
     * The sector this row came from, empty for a row the user added. Carries identity across
     * the round trip so that editing an existing lighthouse updates its sectors instead of
     * discontinuing every one of them and creating replacements.
     */
    globalid: string;
    fromAngle: string;
    toAngle: string;
    fromLineLength: NumericDraft;
    toLineLength: NumericDraft;
    innerRadius: NumericDraft;
    color: SectorColor;
    description: string;
    /**
     * The start angle a seeded row came with, exactly as stored, and the text it was shown as.
     * While `fromAngle` still reads `raw` the stored angle is saved back bit for bit, so precision
     * the table does not display is never rounded away. Absent on rows the user added.
     */
    seededFromAngle?: { raw: string; angle: number };
}

// Deliberately stricter than parseFloat: "3." and "-" are in-progress input, not numbers, so
// they are neither validated as values nor rewritten by a unit switch.
const strictNumberPattern = /^-?\d+(\.\d+)?$/;

export const parseStrictNumber = (raw: string): number | null =>
    strictNumberPattern.test(raw.trim()) ? Number.parseFloat(raw.trim()) : null;

const toMeters = (value: number, unit: LengthUnit) => (unit === "Nm" ? value * metersPerNauticalMile : value);

const formatInUnit = (meters: number, unit: LengthUnit) => {
    const inUnit = unit === "Nm" ? (meters / metersPerNauticalMile).toFixed(3) : meters.toFixed(0);
    return inUnit.includes(".") ? inUnit.replace(/0+$/, "").replace(/\.$/, "") : inUnit;
};

/**
 * Two lengths in metres count as the same when they are within a metre. Exact equality is
 * unusable: the Nm column rounds to three decimals, so a length re-typed from what a cell
 * displays lands up to 0.93 m off the value it came from, and the user has no way to type that
 * difference away. A metre is also below anything the drawn geometry can express.
 */
export const sameLengthInMeters = (a: number | null | undefined, b: number | null | undefined) =>
    a === null || a === undefined || b === null || b === undefined ? a === b : Math.abs(a - b) < 1;

export const numericDraftFromRaw = (raw: string, unit: LengthUnit): NumericDraft => {
    const parsed = parseStrictNumber(raw);
    return { raw, meters: parsed === null ? null : toMeters(parsed, unit) };
};

export const numericDraftFromMeters = (meters: number | null | undefined, unit: LengthUnit): NumericDraft => {
    if (meters === null || meters === undefined) return { raw: "", meters: null };
    return { raw: formatInUnit(meters, unit), meters };
};

/** Re-renders every length and radius in `unit`. Values that do not parse are left untouched. */
export const convertDraftsToUnit = (drafts: SectorDraft[], unit: LengthUnit): SectorDraft[] =>
    drafts.map((draft) => ({
        ...draft,
        fromLineLength:
            draft.fromLineLength.meters === null
                ? draft.fromLineLength
                : numericDraftFromMeters(draft.fromLineLength.meters, unit),
        toLineLength:
            draft.toLineLength.meters === null
                ? draft.toLineLength
                : numericDraftFromMeters(draft.toLineLength.meters, unit),
        innerRadius:
            draft.innerRadius.meters === null
                ? draft.innerRadius
                : numericDraftFromMeters(draft.innerRadius.meters, unit),
    }));

/**
 * A blank row. The radius starts at the configured default - it is the same for most sectors
 * and rarely the value a new row is added to change - while the angles and boundary lengths
 * stay empty, since only the user knows those.
 */
export const emptySectorDraft = (unit: LengthUnit): SectorDraft => ({
    key: uuidv4(),
    globalid: "",
    fromAngle: "",
    toAngle: "",
    fromLineLength: numericDraftFromMeters(null, unit),
    toLineLength: numericDraftFromMeters(null, unit),
    innerRadius: numericDraftFromMeters(DefaultSectorValues.defaultInnerRadius, unit),
    color: SectorColor.WHITE,
    description: "",
});

/**
 * A row to place between two existing ones. The sectors of a lighthouse cover the circle
 * without gaps, so the three boundary values a neighbour dictates are filled in: the new row
 * starts where `previous` ends, and the two boundary lines it shares keep their neighbours'
 * lengths. Only the end angle is left to the user, since that is the actual decision. The
 * neighbours' colour and description are not inherited - those belong to the sector - and the
 * radius comes from the blank row's default.
 */
export const insertedSectorDraft = (previous: SectorDraft, next: SectorDraft, unit: LengthUnit): SectorDraft => ({
    ...emptySectorDraft(unit),
    fromAngle: previous.toAngle,
    fromLineLength: previous.toLineLength,
    toLineLength: next.fromLineLength,
});

/**
 * Sets one angle cell and mirrors the raw text onto the neighbouring row that shares the
 * boundary: a row's `from` is the previous row's `to`, and its `to` is the next row's `from`.
 * Neighbours are by row position, wrapping at both ends like the circle does, and rows are never
 * reordered - the table must not jump while the user types. A lone row has no neighbour, so
 * only the edited cell changes.
 */
export const updateLinkedAngle = (
    drafts: SectorDraft[],
    key: string,
    field: "fromAngle" | "toAngle",
    raw: string
): SectorDraft[] => {
    const index = drafts.findIndex((draft) => draft.key === key);
    if (index === -1) return drafts;

    const updated = drafts.map((draft, i) => (i === index ? { ...draft, [field]: raw } : draft));
    if (drafts.length === 1) return updated;

    const neighbourIndex =
        field === "fromAngle" ? (index - 1 + drafts.length) % drafts.length : (index + 1) % drafts.length;
    const neighbourField = field === "fromAngle" ? "toAngle" : "fromAngle";
    updated[neighbourIndex] = { ...updated[neighbourIndex], [neighbourField]: raw };
    return updated;
};

/**
 * Sets one boundary line length and mirrors it onto the neighbouring row that shares the line,
 * the same way `updateLinkedAngle` links the angles. A dark row has no lengths of its own - the
 * lines around it belong to the lit sectors on either side - so nothing is mirrored onto it.
 */
export const updateLinkedLineLength = (
    drafts: SectorDraft[],
    key: string,
    field: "fromLineLength" | "toLineLength",
    value: NumericDraft
): SectorDraft[] => {
    const index = drafts.findIndex((draft) => draft.key === key);
    if (index === -1) return drafts;

    const updated = drafts.map((draft, i) => (i === index ? { ...draft, [field]: value } : draft));
    if (drafts.length === 1) return updated;

    const neighbourIndex =
        field === "fromLineLength" ? (index - 1 + drafts.length) % drafts.length : (index + 1) % drafts.length;
    if (updated[neighbourIndex].color === SectorColor.DARK) return updated;

    const neighbourField = field === "fromLineLength" ? "toLineLength" : "fromLineLength";
    updated[neighbourIndex] = { ...updated[neighbourIndex], [neighbourField]: value };
    return updated;
};

/**
 * Seeds the table from existing sectors. A sector stores only its start, so each row's `to`
 * columns are read from the next sector, wrapping so the last row closes onto the first.
 *
 * The rows always open sorted by start angle, folded into [0, 360) the same way
 * `sectorDraftsToSectors` does. The sectors usually arrive sorted, but not always (an angle typed
 * in the sector menu is not re-sorted), and an unsorted seed would read each `to` from the wrong
 * sector. The caller's array is left as it is.
 *
 * A sector missing `startLineLength` or `innerRadius` falls back to the same defaults
 * `lighthouseDrawing.ts` and `saveLighthouse` already substitute when they are absent, so a
 * table seeded from sectors without them (e.g. `AppConfig.Lighthouse.DefaultSectors`) opens
 * showing exactly what "Standardsektorer" would draw, instead of eighteen blocking errors.
 *
 * The rows then start at the sector numbered 1 (the first of `sectorOrder`, see applySectorOrder)
 * but stay a rotation of angle order, as each row's Fra/Til is linked to its on-screen neighbours.
 */
/** An angle for display, without the float noise of earlier saves (e.g. 12.300000000000011) */
const formatAngle = (angle: number | undefined) => (angle === undefined ? "" : String(Number(angle.toFixed(10))));

export const sectorDraftsFromSectors = (
    sectors: Sector[],
    unit: LengthUnit,
    sectorOrder?: string[] | null
): SectorDraft[] => {
    const angleOf = (sector: Sector) => (((sector.startAngle ?? 0) % 360) + 360) % 360;
    const sorted = [...sectors].sort((a, b) => angleOf(a) - angleOf(b));
    const start = Math.max(sorted.indexOf(applySectorOrder({ sectors: sorted }, sectorOrder)[0]), 0);
    const rotated = sorted.slice(start).concat(sorted.slice(0, start));

    return rotated.map((sector, index) => {
        const next = rotated[(index + 1) % rotated.length];
        const fromAngle = formatAngle(sector.startAngle);
        return {
            key: uuidv4(),
            globalid: sector.globalid ?? "",
            fromAngle,
            toAngle: formatAngle(next.startAngle),
            fromLineLength: numericDraftFromMeters(
                sector.startLineLength ?? DefaultSectorValues.defaultLineLength,
                unit
            ),
            toLineLength: numericDraftFromMeters(next.startLineLength ?? DefaultSectorValues.defaultLineLength, unit),
            innerRadius: numericDraftFromMeters(sector.innerRadius ?? DefaultSectorValues.defaultInnerRadius, unit),
            color: sector.color ?? SectorColor.WHITE,
            description: sector.description ?? "",
            ...(sector.startAngle === undefined
                ? {}
                : { seededFromAngle: { raw: fromAngle, angle: sector.startAngle } }),
        };
    });
};

/**
 * The start angle a row saves. An angle the user has not changed is the stored one, untouched.
 * A typed angle in [0, 360) is kept exactly as typed; only one outside it is folded in, and the
 * fold's float noise stripped, so "360" saves as 0 and "372.3" as 12.3.
 */
const startAngleOfDraft = (draft: SectorDraft) => {
    if (draft.seededFromAngle && draft.fromAngle === draft.seededFromAngle.raw) return draft.seededFromAngle.angle;

    const angle = parseStrictNumber(draft.fromAngle) ?? 0;
    return angle > 0 && angle < 360 ? angle : Number((((angle % 360) + 360) % 360).toFixed(10));
};

/**
 * Each row's sector number: lit rows counted from 1 in row order, which opens at the sector
 * numbered 1 (see sectorDraftsFromSectors) and never reorders. Dark rows have no number.
 */
export const sectorDraftNumbers = (drafts: SectorDraft[]): (number | null)[] => {
    let number = 0;
    return drafts.map((draft) => (draft.color === SectorColor.DARK ? null : ++number));
};

/**
 * Converts validated drafts to sectors. The `to` columns are dropped: validation has already
 * proven each equals the next row's `from`, so they carry nothing the `from` columns do not.
 * `endLineLength` is left unset because it is never persisted. Each row's globalid travels
 * through so an edit can be matched to the sector it came from; `lighthouseId` is left to the
 * caller, which knows whether this is a new lighthouse or an existing one.
 */
export const sectorDraftsToSectors = (drafts: SectorDraft[]): Sector[] => {
    // 360 and 0 are the same bearing, but only one of them sorts as north. Folding the angle
    // into [0, 360) (see startAngleOfDraft) keeps a sector typed as "360" from being ordered,
    // indexed and numbered as the last sector of the circle instead of the first.
    const sorted = [...drafts].sort((a, b) => startAngleOfDraft(a) - startAngleOfDraft(b));

    return sorted.map((draft, index) => {
        const previous = sorted[(index - 1 + sorted.length) % sorted.length];

        return {
            globalid: draft.globalid,
            lighthouseId: "",
            color: draft.color,
            startAngle: startAngleOfDraft(draft),
            innerRadius: draft.innerRadius.meters ?? undefined,
            // A dark sector has no length cells of its own, but saveLighthouse reads its start
            // length as the end line of the lit sector before it - so that is where it comes
            // from. Anything left on the dark row is stale and deliberately ignored.
            startLineLength:
                (draft.color === SectorColor.DARK ? previous.toLineLength.meters : draft.fromLineLength.meters) ??
                undefined,
            index,
            description: draft.description,
            nfs_status: NFSStatus.PROJECTED,
            nfs_status_line1: NFSStatus.PROJECTED,
            nfs_status_line2: NFSStatus.PROJECTED,
        };
    }) as Sector[];
};
